import { find, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { GoToLeadmailer } from '@icons/common';

import { formatDate } from '../../../../utils/date';
import { pluralize } from '../../../../utils/userflow';

const DATE_FORMAT = 'd MMM';
const NIGHT_WORD_FORMS = ['ночь', 'ночи', 'ночей'];
const AGE_WORD_FORMS = ['год', 'года', 'лет'];
const ADULT_WORD_FORMS = ['взрослый', 'взрослых', 'взрослых'];
const KID_WORD_FORMS = ['ребёнок', 'ребёнка', 'ребёнка'];
const EMPTY_VALUE = '-';

const getOptionValue = option => option?.value ?? option?.id;
const getOptionLabel = option =>
  option?.label ??
  option?.title ??
  option?.shortTitle ??
  option?.name ??
  option?.name_ru;

const getOptionLabelByValue = (options = [], value) => {
  if (value === null || value === undefined || value === '') return null;

  const option = find(
    options,
    item => `${getOptionValue(item)}` === `${value}`
  );

  return getOptionLabel(option) || value;
};

const getDisplayValue = value => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;

  return value;
};

const formatDates = dates => {
  if (isEmpty(dates)) return EMPTY_VALUE;

  const [startDate, endDate] = dates;

  if (!startDate && !endDate) return EMPTY_VALUE;
  if (!startDate || !endDate) {
    return formatDate(startDate || endDate, DATE_FORMAT);
  }

  return `${formatDate(startDate, DATE_FORMAT)} – ${formatDate(
    endDate,
    DATE_FORMAT
  )}`;
};

const formatNights = ({ nights, isNightsFlex }) => {
  if (!nights) return EMPTY_VALUE;

  const count = isNightsFlex ? `${nights - 2} – ${nights + 2}` : nights;
  const label = pluralize(isNightsFlex ? nights + 2 : nights, NIGHT_WORD_FORMS);

  return `${count} ${label}`;
};

const formatTourists = ({ adults, kids }) => {
  const tourists = [];

  if (adults) {
    tourists.push(`${adults} ${pluralize(adults, ADULT_WORD_FORMS)}`);
  }

  if (!isEmpty(kids)) {
    const kidsCount = kids.length;
    const ages = kids.join(', ');
    const lastAge = kids[kids.length - 1];

    tourists.push(
      `${kidsCount} ${pluralize(
        kidsCount,
        KID_WORD_FORMS
      )} (${ages} ${pluralize(lastAge, AGE_WORD_FORMS)})`
    );
  }

  return tourists.join(', ') || EMPTY_VALUE;
};

const formatPrice = price => {
  if (price === null || price === undefined || price === '') return EMPTY_VALUE;

  return new Intl.NumberFormat('ru-RU').format(price);
};

const TourSelectionForm = ({
  className,
  adults,
  cityFromId,
  dates,
  comment,
  destination,
  isNightsFlex,
  kids,
  nights,
  options,
  partnerId,
  hotelMealId,
  price,
  segmentId,
  sourceId,
  onGoToLeadMailer,
}) => {
  const { departures, hotelMeals, partners, segments, sources } = options;

  const fields = [
    {
      label: 'Город вылета',
      value: getOptionLabelByValue(departures, cityFromId),
    },
    {
      label: 'Направление',
      value: destination?.label,
    },
    {
      label: 'Даты вылета',
      value: formatDates(dates),
    },
    {
      label: 'Ночи',
      value: formatNights({ nights, isNightsFlex }),
    },
    {
      label: 'Питание',
      value: getOptionLabelByValue(hotelMeals, hotelMealId),
    },
    {
      label: 'Бюджет',
      value: formatPrice(price),
    },
    {
      label: 'Туристы',
      value: formatTourists({ adults, kids }),
    },
    {
      label: 'Источник обращения',
      value: getOptionLabelByValue(sources, sourceId),
    },
    {
      label: 'Партнер',
      value: getOptionLabelByValue(partners, partnerId),
    },
    {
      label: 'Сегмент группа',
      value: getOptionLabelByValue(segments, segmentId),
    },
  ];

  return (
    <StyledTourSelectionForm className={className}>
      <StyledHeadline>
        Подбор тура
        <StyledGoToLeadMailerIcon onClick={onGoToLeadMailer} />
      </StyledHeadline>
      {fields.map(({ label, value }) => (
        <StyledField key={label}>
          <StyledLabel>{label}</StyledLabel>
          <StyledValue>{getDisplayValue(value)}</StyledValue>
        </StyledField>
      ))}
      <StyledNote>
        <StyledLabel>Заметка к тикету</StyledLabel>
        <StyledValue>{getDisplayValue(comment)}</StyledValue>
      </StyledNote>
    </StyledTourSelectionForm>
  );
};

const optionsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    name: PropTypes.string,
    name_ru: PropTypes.string,
    shortTitle: PropTypes.string,
    title: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired
).isRequired;

TourSelectionForm.propTypes = {
  className: PropTypes.string,
  adults: PropTypes.number,
  cityFromId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  comment: PropTypes.string,
  dates: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
      .isRequired
  ),
  destination: PropTypes.shape({
    id: PropTypes.string,
    isHotel: PropTypes.bool,
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  options: PropTypes.shape({
    countries: optionsPropTypes,
    departures: optionsPropTypes,
    destinations: optionsPropTypes,
    hotelMeals: optionsPropTypes,
    sources: optionsPropTypes,
    partners: optionsPropTypes,
    segments: optionsPropTypes,
  }),
  isNightsFlex: PropTypes.bool,
  kids: PropTypes.arrayOf(PropTypes.number.isRequired),
  nights: PropTypes.number,
  hotelMealId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  price: PropTypes.number,
  sourceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  segmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  partnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onGoToLeadMailer: PropTypes.func,
};

TourSelectionForm.defaultProps = {
  className: '',
  onGoToLeadMailer: Function.prototype,
  options: {
    countries: [],
    departures: [],
    destinations: [],
    hotelMeals: [],
    partners: [],
    segments: [],
    sources: [],
  },
};

const StyledTourSelectionForm = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledHeadline = styled.div`
  align-items: center;
  color: #919499;
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  margin-bottom: 7px;
`;

const StyledField = styled.div`
  display: grid;
  font-size: 14px;
  gap: 8px;
  grid-template-columns: 135px minmax(0, 1fr);
  line-height: 20px;
  min-height: 25px;
`;

const StyledLabel = styled.span`
  color: #919499;
`;

const StyledValue = styled.span`
  color: #17181a;
  overflow-wrap: anywhere;
`;

const StyledNote = styled(StyledField)`
  border-top: 1px solid #d9d9d9;
  margin-top: 10px;
  padding-top: 13px;
`;

const StyledGoToLeadMailerIcon = styled(GoToLeadmailer)`
  cursor: pointer;
  height: 24px;
  width: 25px;
`;

export default TourSelectionForm;
